<script setup lang="ts">
import { onErrorCaptured, onUnmounted, ref } from 'vue';
import * as Sentry from '@sentry/browser';

import SmartAppDialogs from '@/components/SmartAppDialogs.vue';
import SmartListBars from '@/components/SmartListBars.vue';
import SmartPageDialogs from '@/components/SmartPageDialogs.vue';
import GoToTopBtn from '@/components/btns/GoToTopBtn.vue';
import { useAuthStore } from '@/stores/useAuthStore';
import { useCustomerDetailsStore } from '@/stores/useCustomerDetailsStore';
import { useLoaderStore } from '@/stores/useLoaderStore';
// import { useSidebarsStore } from '@/stores/useSidebarsStore';
import { useStaticStore } from '@/stores/useStaticStore';
import { useUserStore } from '@/stores/useUserStore';
import { useWebSocketStore } from '@/stores/useWebSocketStore';
import { useStore } from '@/store';
import fetchPortalVersionInterval from '@/helpers/fetchPortalVersionInterval';
import { fetchGetUserProfile } from '@/helpers/fetchers';
import { btnLogout } from '@/helpers/logouts';
import { sendError } from '@/helpers/sentry';
import { KHotkeyError } from '@/helpers/throwables';

export interface Props {
  absolute?: boolean;
}
const props = withDefaults(defineProps<Props>(), {});

const staticStore = useStaticStore();
const authStore = useAuthStore();
const userStore = useUserStore();
const webSocketStore = useWebSocketStore();
const loaderStore = useLoaderStore();
// const sidebarsStore = useSidebarsStore();
const customerDetailsStore = useCustomerDetailsStore();
const store = useStore();

const isStaticLoading = ref(true); // HACK what if the static data fails to load

fetchPortalVersionInterval();
void fetchGetUserProfile()
  .catch(async (err: unknown) => {
    await btnLogout();
    throw err;
  })
  .then((userData) => {
    userStore.setUser(userData);
    Sentry.setUser({
      staff: authStore.isStaff,
      customerCode: authStore.authData.customerCode,
      username: authStore.authData.name,
    });
    void store.dispatch('moduleSocket/socketConnect');
    webSocketStore.init();
    loaderStore.increaseGlobalLoader();
    void Promise.all([
      staticStore.requestAllStaticData(),
      customerDetailsStore.getCustomerDetails(),
    ]).finally(() => {
      loaderStore.decreaseGlobalLoader();
      isStaticLoading.value = false;
    });
  });

onUnmounted(() => {
  void store.dispatch('moduleSocket/socketDisconnect');
});

onErrorCaptured((err, componentPublicInstance, info) => {
  if (err instanceof KHotkeyError) {
    console.error(err);
    sendError(err, 'onErrorCaptured', info);
    return false;
  }
});
</script>

<template>
  <SmartAppDialogs />
  <SmartPageDialogs />
  <SmartListBars :absolute="props.absolute" />
  <v-main :class="$style.main">
    <template v-if="staticStore.isAllStaticDataLoaded || !isStaticLoading">
      <router-view></router-view>
    </template>
  </v-main>
  <!-- TODO should it be moved to the left? What about notifications? -->
  <!-- :style="sidebarsStore.sidebars.helpcenter ? { right: 375 + 12 + 'px' } : undefined" -->
  <GoToTopBtn />
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.main {
  min-height: 300px;
  background-color: colors.$color-gray-50;
}

@media print {
  .main {
    --v-layout-left: 0px !important;
    --v-layout-right: 0px !important;
    /* --v-layout-top: 0px !important; */
    --v-layout-bottom: 0px !important;
  }
}
</style>
